import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Activity, Footprints, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ClientSpecialtiesPanelProps {
  clientId: string;
  canEdit: boolean;
}

const ALL_SPECIALTIES = [
  { value: 'orthopedics', label: 'Orthopedics', icon: Activity },
  { value: 'podiatry', label: 'Podiatry', icon: Footprints },
] as const;

export function ClientSpecialtiesPanel({ clientId, canEdit }: ClientSpecialtiesPanelProps) {
  const { toast } = useToast();
  const [active, setActive] = useState<string[]>(['orthopedics']);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('clients')
        .select('active_specialties')
        .eq('id', clientId)
        .maybeSingle();
      if (!cancelled && data?.active_specialties) {
        setActive(data.active_specialties as string[]);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const toggle = (value: string) => {
    setActive(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const handleSave = async () => {
    if (!canEdit) return;
    if (active.length === 0) {
      toast({ title: 'At least one specialty required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('clients')
      .update({ active_specialties: active })
      .eq('id', clientId);
    setSaving(false);
    if (error) {
      toast({ title: 'Failed to save', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Specialties updated', description: 'Active specialties saved for this client.' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Active Specialties</CardTitle>
        <CardDescription>
          Controls which clinical workflows (CPT catalog filters, modifiers, AI extractors) are
          available to surgeons in this tenant.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {ALL_SPECIALTIES.map(opt => {
            const Icon = opt.icon;
            const checked = active.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex items-center gap-3 p-3 rounded-md border border-border bg-card cursor-pointer"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(opt.value)}
                  disabled={!canEdit}
                />
                <Icon className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">{opt.label}</span>
              </label>
            );
          })}
        </div>
        <Button onClick={handleSave} disabled={!canEdit || saving} className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save Specialties'}
        </Button>
      </CardContent>
    </Card>
  );
}
